import React, { useEffect } from 'react';
import AuthenticatedApp from './components/authenticated/authenticated-app';
import UnauthenticatedApp from './components/unauthenticated/unauthenticated-app';
import createAppStateStore from './app-state-store';
import { cetraLightModeTheme } from './theme';
import { observer } from 'mobx-react-lite';
import AppAuthState from './react-core-lib/components/global/AuthState';
import ContentManager from './components/state-mgmt/content-manager';
import AuthenticatedAppWithStore from './react-core-lib/components/app/authenticated-app-with-store';
import configureAmplify from './react-core-lib/third-party/amplify';
import mixpanel from 'mixpanel-browser';
import ReactGA4 from 'react-ga4';
import './App.css';
import './console-overrides';
import { register } from 'swiper/element/bundle';
import AccessibilityWidget from './AccessibilityWidget';
import config from './config';
import {
    getProvetParamsFromSearch,
    persistProvetParams,
    clearProvetParams,
    markProvetParamsSeen,
} from './components/helper/onboardingParamsHelper';

register();
configureAmplify();

function App() {
    const captureProvetParams = () => {
        try {
            const params = getProvetParamsFromSearch(window.location.search || '');
            const hasParams = Object.values(params).some(Boolean);
            if (hasParams) {
                console.log('[ProVet] New parameters detected, updating localStorage...');
                clearProvetParams();
                persistProvetParams(params);
                markProvetParamsSeen();
            }
        } catch (_) {
            /* noop */
        }
    };

    useEffect(() => {
        // Initial capture on mount
        captureProvetParams();

        // Capture UTM parameters on initial page load (only if not already stored)
        const captureUtmParams = () => {
            const existingUtmParams = localStorage.getItem('utmParams');
            if (!existingUtmParams) {
                const urlParams = new URLSearchParams(window.location.search);
                const utmParams = {};
                ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((param) => {
                    const value = urlParams.get(param);
                    if (value) {
                        utmParams[param] = value;
                    }
                });
                if (Object.keys(utmParams).length > 0) {
                    localStorage.setItem('utmParams', JSON.stringify(utmParams));
                    console.log('[UTM] Captured UTM parameters:', utmParams);
                }
            }
        };
        captureUtmParams();

        const { clinicType, isProd, mixpanelToken, gtagId } = config;

        if (mixpanelToken) {
            mixpanel.init(mixpanelToken, {
                api_host: 'https://api-eu.mixpanel.com',
                verbose: true,
                debug: true,
                track_pageview: 'url-with-path',
                persistence: 'localStorage',
                ignore_dnt: true,
                record_sessions_percent: 100,
                record_canvas: true,
                record_mask_all_text: false,
                record_mask_all_inputs: false,
                record_mask_text_selector: '.mp-mask-patient, .mp-mask-billing, .mp-mask-password',
                record_mask_input_selector: '.mp-mask-patient, .mp-mask-billing, .mp-mask-password',
                record_block_selector: '[data-mp-never-block]',
                record_console: false,
            });
            console.log(`Mixpanel initialized with token: ${mixpanelToken}`, clinicType, isProd);
        }

        if (gtagId) {
            ReactGA4.initialize(gtagId);
        }

        // Listen for URL changes (e.g., when ProVet navigates to new client)
        const handleLocationChange = () => {
            captureProvetParams();
        };

        // Listen for tab visibility/focus changes (in case ProVet updates URL when switching)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                captureProvetParams();
            }
        };

        const handleFocus = () => {
            captureProvetParams();
        };

        // Add event listeners
        window.addEventListener('popstate', handleLocationChange);
        window.addEventListener('hashchange', handleLocationChange);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);

        // Cleanup
        return () => {
            window.removeEventListener('popstate', handleLocationChange);
            window.removeEventListener('hashchange', handleLocationChange);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, []);

    return (
        <>
            <AuthenticatedAppWithStore
                AuthenticatedComponent={AuthenticatedApp}
                UnauthenticatedComponent={UnauthenticatedApp}
                createAppStateStore={createAppStateStore}
                theme={cetraLightModeTheme}
                globalComponents={
                    <>
                        <AppAuthState />
                        <AccessibilityWidget />
                    </>
                }
                globalAuthenticatedComponents={<ContentManager />}
            />
        </>
    );
}

export default observer(App);
